/** eslint-disable no-undef */
import React, { useId } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  Params,
  Location,
  NavigateFunction,
} from 'react-router-dom';

export interface NavigationProps {
  router: {
    /** a Location object that represents the current URL. */
    location: Location;
    /** a NavigateFunction that allows for programmatic navigation. */
    navigate: NavigateFunction;
    /**
     * a Readonly object of type Params<string> that contains
     * the dynamic segments of the current URL.
     * */
    params: Readonly<Params<string>>;
  }
}
/**
 * The withRouter function is a higher-order component that takes in a Component of type
 * React.ComponentType<T & NavigationProps>.
 * It returns a new component called ComponentWithRouterProp that wraps the original Component
 * and passes the router property to it.
 * The ComponentWithRouterProp component uses the `useLocation, useNavigate, and useParams` hooks
 * from the React Router library to
 * extract the location, navigate, and params properties from the router and pass them to
 * the wrapped Component as props.
 * The withRouter function is exported as a default export.
 *
 * @param Component
 * @returns
*/
function withRouter<T extends Record<string, any>>(
  Component: React.ComponentType<T & NavigationProps>,
) {
  function ComponentWithRouterProp(props: T) {
    const id = useId();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams<Record<string, string>>();
    return (
      <Component
        key={id}
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }
  return ComponentWithRouterProp;
}

export default withRouter;
